import React, { ReactElement, useState } from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';

import {
  gray40,
  gray80,
  white,
  yellow50,
  yellow_overlay10,
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { boldMd, regularLg, regularXl, regularMd, regularSm } from '../../../constants/location';
import { patternMessages } from '../../../constants/messages';

// 置き換えの種類。文言と実行ボタンのラベルを切り替える
export type ItemOverwriteMode = 'aiRead' | 'reset';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  mode?: ItemOverwriteMode;
  loading?: boolean;
};

const styles = {
  buttonWrapper: {
    display: 'flex',
    gap: '16px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
    },
  },
  cancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  cancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  confirmButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  confirmButtonText: {
    ...boldMd,
    color: white,
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      borderRadius: '8px',
      boxSizing: 'border-box',
      margin: '16px',
      padding: '24px',
      width: '560px',
      maxWidth: '90vw',
      '@media screen and (max-width: 960px)': {
        padding: '24px 16px',
      },
    },
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
    '@media screen and (max-width:960px)': {
      marginBottom: '16px',
    },
  },
  icon: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  iconButton: {
    padding: 0,
  },
  title: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  message: {
    ...regularMd,
    backgroundColor: yellow_overlay10,
    borderRadius: '4px',
    color: yellow50,
    marginBottom: '16px',
    padding: '16px',
  },
  note: {
    ...regularSm,
    color: gray80,
    marginBottom: '32px',
  },
};

// モードごとの表示文言
const MODE_TEXTS: Record<
  ItemOverwriteMode,
  { title: string; message: string; note: string; confirmLabel: string }
> = {
  aiRead: {
    title: patternMessages.aiReadOverwriteTitle,
    message: patternMessages.aiReadOverwriteMessage,
    note: patternMessages.aiReadOverwriteNote,
    confirmLabel: '読み取り直す',
  },
  reset: {
    title: patternMessages.itemResetTitle,
    message: patternMessages.itemResetMessage,
    note: patternMessages.itemResetNote,
    confirmLabel: 'リセットする',
  },
};

/**
 * 編集中の読取パターンで項目設定が丸ごと置き換わる操作の前に出す確認モーダル（#3147）
 * AI読取・手動入力リセットはいずれも既存の項目IDごと置き換えるため、実行前に影響を明示する
 */
const PatternItemOverwriteConfirmModal = ({
  open,
  onClose,
  onConfirm,
  mode = 'aiRead',
  loading = false,
}: Props): ReactElement => {
  const proxyDialogSx = useProxyDialogSx();

  // 閉じるアニメーション中も直前の文言を保持する。
  // 閉じる際に呼び出し元が mode を戻すと、フェードアウト中に別モードの文言が一瞬見えてしまうため、
  // 表示中のモードは open のあいだだけ更新する（描画前に確定させたいのでレンダー中に同期する）。
  const [displayedMode, setDisplayedMode] = useState<ItemOverwriteMode>(mode);
  if (open && displayedMode !== mode) {
    setDisplayedMode(mode);
  }
  const texts = MODE_TEXTS[displayedMode];

  return (
    <Dialog open={open} onClose={onClose} sx={[styles.dialog, proxyDialogSx]}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>{texts.title}</Typography>
        <IconButton onClick={onClose} sx={styles.iconButton}>
          <Close sx={styles.icon} />
        </IconButton>
      </Box>
      <Typography sx={styles.message}>{texts.message}</Typography>
      <Typography sx={styles.note}>{texts.note}</Typography>
      <Box sx={styles.buttonWrapper}>
        <Button sx={styles.cancelButton} onClick={onClose} disabled={loading}>
          <Typography sx={styles.cancelButtonText}>キャンセル</Typography>
        </Button>
        <Button sx={styles.confirmButton} onClick={onConfirm} disabled={loading}>
          <Typography sx={styles.confirmButtonText}>{texts.confirmLabel}</Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default PatternItemOverwriteConfirmModal;
